import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../../api.js';
import { useBackgroundJobs } from '../jobs/BackgroundJobsContext';

/**
 * ExportModal — prepare a zip to download.
 *
 * Two formats (plans/BY_DOCUMENT_INVENTORY_VIEW_PROPOSAL.md §7b-1):
 *   package   — the documents, numbered and named, with an index page that opens
 *               in a browser (works offline) and a manifest. The default.
 *   originals — the files as uploaded, mirroring the folder structure (or flat).
 *
 * Fire-and-forget (Background Jobs UX): pick options, hit "Prepare download",
 * and the modal submits the `export_documents` job and closes. Progress shows
 * in the BackgroundJobsBanner; on completion a toast fires and the download
 * auto-starts (jobRegistry `autoDownload`) — no per-modal poll loop, and the
 * download lands even if you've navigated away.
 *
 * Scope-agnostic: caller passes scope ('case' | 'folder' | 'selection' | 'file')
 * plus folderPath / documentIds / documentId as needed. 'file' = the documents
 * inside one cut file; it is always a package.
 *
 * What the zip will hold — the count and the size — is asked of the server as
 * the options change (`/export/preview`, the same measure the export is held
 * to) and shown before the button, with how long preparing it should take;
 * over the limit, the button stays off.
 */
const fmtBytes = (n) => {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${Math.round(n / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round((n || 0) / 1024))} KB`;
};

// The server's estimate of the build, in words a person plans by.
const fmtWait = (seconds) => {
  if (seconds < 45) return 'under a minute';
  const minutes = Math.round(seconds / 60);
  return `about ${minutes} minute${minutes === 1 ? '' : 's'}`;
};

// "Original files only" holds files as uploaded: the documents cut out of a file are not in it
// one by one. Say how many are filed in what is being exported, and whether their file is.
function leftOutText(left, scope) {
  const docs = left.documents;
  const here = scope === 'folder' ? 'in this folder' : 'in this case';
  const were = docs === 1 ? '1 document' : `${docs} documents`;
  const files = left.files === 1 ? '1 file' : `${left.files} files`;
  if (left.files_elsewhere > 0) {
    const gone = left.documents_of_files_elsewhere;
    const elsewhere = left.files_elsewhere === left.files
      ? (left.files === 1 ? 'that file is filed in another folder, so it is' : 'those files are filed in other folders, so they are')
      : `${left.files_elsewhere} of those files ${left.files_elsewhere === 1 ? 'is' : 'are'} filed in other folders, so ${gone === 1 ? '1 document is' : `${gone} documents are`}`;
    return `${were} ${here} ${docs === 1 ? 'was' : 'were'} cut out of ${files}. This export holds files as uploaded — ${elsewhere} not in it.`;
  }
  return `${were} ${here} ${docs === 1 ? 'was' : 'were'} cut out of ${files}. This export holds ${left.files === 1 ? 'that file' : 'those files'} whole, as uploaded, not the documents one by one.`;
}

export default function ExportModal({
  caseId, scope, folderPath, documentIds, documentId, count, label, onClose,
}) {
  const { submitJob } = useBackgroundJobs();
  const packageOnly = scope === 'file';
  const [format, setFormat] = useState('package');
  const [structure, setStructure] = useState('mirror');
  const [includeSummaries, setIncludeSummaries] = useState(true);
  const [includeOriginals, setIncludeOriginals] = useState(false);
  const [documentsOnly, setDocumentsOnly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // { loading } | { failed } | the server's measure: document_count, total_bytes, limit_bytes, over_limit,
  // estimated_seconds, not_included, repeats_left_out
  const [measure, setMeasure] = useState({ loading: true });

  function requestBody() {
    const body = { scope, format };
    if (scope === 'folder') body.folder_path = folderPath;
    if (scope === 'selection') body.document_ids = documentIds;
    if (scope === 'file') body.document_id = documentId;
    if (format === 'package') {
      body.include_summaries = includeSummaries;
      body.include_originals = includeOriginals;
      body.documents_only = documentsOnly;
    } else {
      body.structure = structure;
    }
    return body;
  }

  // Only what changes the contents of the zip re-asks (not summaries, not the layout);
  // the selection by value — a caller may hand a new array on every render.
  const idsKey = (documentIds || []).join(',');
  useEffect(() => {
    let stale = false;
    setMeasure({ loading: true });
    api.post(`/documents/case/${caseId}/export/preview`, requestBody())
      .then((r) => { if (!stale) setMeasure(r || { failed: true }); })
      .catch(() => { if (!stale) setMeasure({ failed: true }); });      // the export route still holds the limit
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, scope, folderPath, documentId, idsKey, format, includeOriginals, documentsOnly]);

  const measured = !measure.loading && !measure.failed;
  const blocked = measured && (measure.over_limit || measure.document_count === 0);

  async function start() {
    setSubmitting(true);
    setError(null);
    try {
      await submitJob(
        'export_documents',
        api.post(`/documents/case/${caseId}/export`, requestBody()),
      );
      onClose?.();
    } catch (e) {
      setError(e.message || 'Could not start export.');
      setSubmitting(false);
    }
  }

  const scopeLabel = scope === 'case' ? 'the entire case'
    : scope === 'folder' ? `the “${folderPath}” folder`
    : scope === 'file' ? `the ${count} document${count === 1 ? '' : 's'} in this file`
    : `${count} selected file${count === 1 ? '' : 's'}`;
  const row = { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13 };
  const sub = { ...row, color: '#555', marginLeft: 24, marginTop: 6 };

  return createPortal((
    <div className="np-export-overlay" onClick={submitting ? undefined : onClose}>
      <div className="np-export-modal" data-testid="export-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500, height: 'auto' }}>
        <div className="np-export-head">
          <h3>Export {label || 'files'}</h3>
          <button className="np-icon-btn" onClick={onClose} title="Close" disabled={submitting}>✕</button>
        </div>

        <div className="np-export-body" style={{ display: 'block', padding: 16 }}>
          <p style={{ marginTop: 0, fontSize: 13 }}>
            Download a zip of <b>{scopeLabel}</b>.
          </p>

          <label style={row}>
            <input
              type="radio" name="export-format" data-testid="export-format-package"
              checked={format === 'package'} onChange={() => setFormat('package')} disabled={submitting}
            />
            <span>
              <b>Browsable package</b> — each document as its own numbered, named file, in the same folders
              you see here, with an index page you open in a browser. Works offline; nothing depends on Chargestack.
            </span>
          </label>
          {format === 'package' && (
            <>
              <label style={sub}>
                <input
                  type="checkbox" data-testid="export-opt-summaries"
                  checked={includeSummaries} onChange={e => setIncludeSummaries(e.target.checked)} disabled={submitting}
                />
                <span>Include summaries and names in the index. Untick for a package going to someone who should not see them.</span>
              </label>
              <label style={sub}>
                <input
                  type="checkbox" data-testid="export-opt-originals"
                  checked={includeOriginals} onChange={e => setIncludeOriginals(e.target.checked)} disabled={submitting}
                />
                <span>Include the original, uncut files the documents were found in.</span>
              </label>
              {!packageOnly && (
                <label style={sub}>
                  <input
                    type="checkbox" data-testid="export-opt-docsonly"
                    checked={documentsOnly} onChange={e => setDocumentsOnly(e.target.checked)} disabled={submitting}
                  />
                  <span>Documents only — leave out audio and video.</span>
                </label>
              )}
            </>
          )}

          {!packageOnly && (
            <>
              <label style={{ ...row, marginTop: 14 }}>
                <input
                  type="radio" name="export-format" data-testid="export-format-originals"
                  checked={format === 'originals'} onChange={() => setFormat('originals')} disabled={submitting}
                />
                <span><b>Original files only</b> — the files as they were uploaded, in your folders.</span>
              </label>
              {format === 'originals' && (
                <label style={sub}>
                  <input
                    type="checkbox"
                    checked={structure === 'flat'}
                    onChange={e => setStructure(e.target.checked ? 'flat' : 'mirror')}
                    disabled={submitting}
                  />
                  <span>Flat (all files at the top level) — otherwise the zip mirrors your folder structure.</span>
                </label>
              )}
            </>
          )}
          <div data-testid="export-measure" style={{ marginTop: 14, fontSize: 13, color: blocked ? '#c0392b' : '#333' }}>
            {measure.loading && <span style={{ color: '#888' }}>Counting…</span>}
            {measured && measure.document_count === 0 && 'There is nothing to export here.'}
            {measured && measure.document_count > 0 && (
              <>
                <b>
                  {measure.document_count} {format === 'package' ? 'document' : 'file'}{measure.document_count === 1 ? '' : 's'}
                  {' · '}{fmtBytes(measure.total_bytes)}
                </b>
                {!measure.over_limit && measure.estimated_seconds > 0 && (
                  <span data-testid="export-wait"> · {fmtWait(measure.estimated_seconds)} to prepare</span>
                )}
                {measure.over_limit && (
                  <> — over the {fmtBytes(measure.limit_bytes)} limit for one download.
                    {format === 'package' && !packageOnly && !documentsOnly ? ' Leave out audio and video, or export' : ' Export'} a folder or a selection instead.
                  </>
                )}
              </>
            )}
            {measured && format === 'originals' && measure.cut_left_out?.documents > 0 && (
              <div data-testid="export-cut-left-out" style={{ color: '#555', marginTop: 4 }}>
                {leftOutText(measure.cut_left_out, scope)}
                {' '}
                <button
                  type="button" data-testid="export-choose-package" onClick={() => setFormat('package')} disabled={submitting}
                  style={{ padding: 0, font: 'inherit', background: 'none', border: 0, color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Choose the package
                </button>
                {' '}to get each document as its own file.
              </div>
            )}
            {measured && measure.repeats_left_out > 0 && (
              <div style={{ color: '#555', marginTop: 4 }}>
                {measure.repeats_left_out === 1
                  ? 'One file was uploaded twice to the same folder — it is included once.'
                  : `${measure.repeats_left_out} repeated uploads of the same file to the same folder are included once.`}
              </div>
            )}
            {measured && measure.not_included > 0 && (
              <div style={{ color: '#555', marginTop: 4 }}>
                {measure.not_included} document{measure.not_included === 1 ? ' has' : 's have'} no file on record and will be left out.
              </div>
            )}
          </div>
          {error && <div className="bulk-popover-error" style={{ marginTop: 10 }}>{error}</div>}
        </div>

        <div className="np-export-foot">
          <span style={{ flex: 1 }} />
          <button className="btn" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" data-testid="export-start" onClick={start} disabled={submitting || blocked}>
            {submitting ? 'Starting…' : 'Prepare download'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
